import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import db from '../firebaseConfig';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { extractRawBarcode } from '../utils/barcodeUtils';
import { buildProductPhotos } from '../utils/productPhotos';
import { compressImageFile } from '../utils/productPhotoFiles';
import { getCollectionName, isSimpleProductCountry, VALID_COUNTRY_IDS } from '../utils/countryConfig';
import { saveProductPatch, deleteLegacyProductsSafely } from '../utils/productPersistence';
import { getProductVariants } from '../utils/productVariants';
import ProductEffects from './ProductEffects';
import ProductAlbumDialog from './ProductAlbumDialog';
import ProductPrepMemberships from './ProductPrepMemberships';
import '../styles.css';

const hasPreparationData = product => ['styleIds', 'effects', 'dataStatus']
  .some(field => Object.prototype.hasOwnProperty.call(product || {}, field));
const PREPARATION_DELETE_MESSAGE = '此商品已啟用備播資料，目前不支援刪除，以免破壞直播清單、樣品或條碼關聯。您可以在編輯頁停用不再使用的樣式；商品與既有紀錄會保留。';

// 獲取集合名稱

function ViewProduct() {
  const { country, id } = useParams();
  if (!VALID_COUNTRY_IDS.includes(country)) return <p role="alert">無效的國家</p>;
  return <ViewProductContent key={`${country}:${id}`} />;
}

function ViewProductContent() {
  const { country, id } = useParams();
  const isChina = country === 'china';
  const isKorea = country === 'korea';
  const isJapan = country === 'japan';
  const isThailand = country === 'thailand';
  const isCrystal = isSimpleProductCountry(country);
  const targetCurrencyName = '台幣';
  const targetCurrencyCode = 'TWD';
  const navigate = useNavigate();
  
  const [product, setProduct] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [airRate, setAirRate] = useState(144);
  const [exchangeRate, setExchangeRate] = useState(30);
  const [airTaiwanPerKg, setAirTaiwanPerKg] = useState(144);
  const [airIntlKrwPerKg, setAirIntlKrwPerKg] = useState(0);
  const [transitTaiwanPerKg, setTransitTaiwanPerKg] = useState(125);
  const [transitIntlKrwPerKg, setTransitIntlKrwPerKg] = useState(0);
  const [seaTaiwanPerKg, setSeaTaiwanPerKg] = useState(95);
  const [seaIntlKrwPerKg, setSeaIntlKrwPerKg] = useState(0);
  const [shippingRate, setShippingRate] = useState(null); // 針對中國
  const [extraPercentage, setExtraPercentage] = useState(0); // 額外%數

  // 內嵌編輯廠商相關 state
  const [editingVendor, setEditingVendor] = useState(false);
  const [vendorInput, setVendorInput] = useState('');
  const [vendorBase, setVendorBase] = useState(null);
  const [isSavingVendor, setIsSavingVendor] = useState(false);
  
  // 刪除商品相關 state
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteVerificationCode, setDeleteVerificationCode] = useState('');
  const [deleteCodeInput, setDeleteCodeInput] = useState('');
  const [selectedPhoto, setSelectedPhoto] = useState('');
  const [showPhotoManager, setShowPhotoManager] = useState(false);
  const [photoDrafts, setPhotoDrafts] = useState([]);
  const [photoBase, setPhotoBase] = useState(null);
  const [newPhotoUrl, setNewPhotoUrl] = useState('');
  const [isSavingPhotos, setIsSavingPhotos] = useState(false);
  const [photoDirty, setPhotoDirty] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const [isReadingPhotos, setIsReadingPhotos] = useState(false);
  const photoContext = useRef(`${country}/${id}`);
  photoContext.current = `${country}/${id}`;

  // 子商品和贈品匹配的商品資料
  const [subProductMatches, setSubProductMatches] = useState([]);
  const [giftMatches, setGiftMatches] = useState([]);

  // 取得廠商列表
  const [vendors, setVendors] = useState([]);
  useEffect(() => {
    const photos = buildProductPhotos(product || {});
    setSelectedPhoto(photos.find((photo) => photo.isCover)?.url || photos[0]?.url || '');
    if (!showPhotoManager && !photoDirty) setPhotoDrafts(photos);
  }, [product, showPhotoManager, photoDirty]);

  useEffect(() => {
    setShowPhotoManager(false); setPhotoDirty(false); setPhotoError(''); setPhotoDrafts([]); setPhotoBase(null);
  }, [country, id]);


  useEffect(() => {
    const fetchVendors = async () => {
      const vendorsRef = collection(db, 'vendors');
      const q = query(vendorsRef, where('country', '==', country));
      const snapshot = await getDocs(q);
      const vendorList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setVendors(vendorList);
    };
    if (country) fetchVendors();
  }, [country]);

  // 根據 localStorage 最後選擇的廠商排序
  const lastSelectedVendor = localStorage.getItem('lastSelectedVendor') || '';
  const sortedVendors = [...vendors].sort((a, b) => {
    if (a.name === lastSelectedVendor) return -1;
    if (b.name === lastSelectedVendor) return 1;
    return a.name.localeCompare(b.name);
  });

  // 讀取匯率與運費設定
  useEffect(() => {
    const fetchRates = async () => {
      const ratesRef = doc(db, 'rates', country);
      const docSnap = await getDoc(ratesRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (isChina) {
          setExchangeRate(data.exchangeRate || 30);
          setShippingRate(data.shippingRate || 1);
          setExtraPercentage(data.extraPercentage || 0);
        } else if (isKorea) {
          setAirTaiwanPerKg(
            data.airTaiwanPerKg !== undefined && data.airTaiwanPerKg !== null
              ? data.airTaiwanPerKg
              : data.airRate ?? 144
          );
          setAirIntlKrwPerKg(
            data.airIntlKrwPerKg !== undefined && data.airIntlKrwPerKg !== null
              ? data.airIntlKrwPerKg
              : 0
          );
          setTransitTaiwanPerKg(
            data.transitTaiwanPerKg !== undefined && data.transitTaiwanPerKg !== null
              ? data.transitTaiwanPerKg
              : data.transitRate ?? 125
          );
          setTransitIntlKrwPerKg(
            data.transitIntlKrwPerKg !== undefined && data.transitIntlKrwPerKg !== null
              ? data.transitIntlKrwPerKg
              : 0
          );
          setSeaTaiwanPerKg(
            data.seaTaiwanPerKg !== undefined && data.seaTaiwanPerKg !== null
              ? data.seaTaiwanPerKg
              : data.seaRate ?? 95
          );
          setSeaIntlKrwPerKg(
            data.seaIntlKrwPerKg !== undefined && data.seaIntlKrwPerKg !== null
              ? data.seaIntlKrwPerKg
              : 0
          );
          setExchangeRate(data.exchangeRate || 30);
          setExtraPercentage(data.extraPercentage || 0);
        } else if (isJapan) {
          setAirRate(data.airRate || 144);
          setExchangeRate(data.exchangeRate || 30);
          setExtraPercentage(data.extraPercentage || 0);
        } else {
          setAirRate(data.airRate || 144);
          setExchangeRate(data.exchangeRate || 0.9);
          setExtraPercentage(data.extraPercentage || 0);
        }
      }
    };
    if (country) fetchRates();
  }, [country]);

  // 讀取產品資料
  useEffect(() => {
    let active = true;
    const fetchProduct = async () => {
      try {
        const collectionName = getCollectionName(country);
        const docRef = doc(db, collectionName, id);
        const docSnap = await getDoc(docRef);
        if (!active) return;
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.country !== undefined && data.country !== country) { setProduct(null); return; }
          setProduct(data);
          setVendorInput(data.vendor || '');
          
          // 載入子商品匹配的商品資料
          if (data.subProducts && data.subProducts.length > 0) {
            const matches = await Promise.all(
              data.subProducts.map(async (subProduct) => {
                try {
                  const productsRef = collection(db, collectionName);
                  const q = query(productsRef, where('itemNumber', '==', subProduct.itemNumber));
                  const snapshot = await getDocs(q);
                  if (!snapshot.empty) {
                    return {
                      subProduct,
                      matchedProduct: snapshot.docs[0].data(),
                      matchedProductId: snapshot.docs[0].id
                    };
                  }
                  return { subProduct, matchedProduct: null };
                } catch (error) {
                  console.error(`載入子商品 ${subProduct.itemNumber} 失敗:`, error);
                  return { subProduct, matchedProduct: null };
                }
              })
            );
            setSubProductMatches(matches);
          }
          
          // 載入贈品匹配的商品資料
          if (data.gifts && data.gifts.length > 0) {
            const matches = await Promise.all(
              data.gifts.map(async (gift) => {
                try {
                  const productsRef = collection(db, collectionName);
                  const q = query(productsRef, where('itemNumber', '==', gift.itemNumber));
                  const snapshot = await getDocs(q);
                  if (!snapshot.empty) {
                    return {
                      gift,
                      matchedProduct: snapshot.docs[0].data(),
                      matchedProductId: snapshot.docs[0].id
                    };
                  }
                  return { gift, matchedProduct: null };
                } catch (error) {
                  console.error(`載入贈品 ${gift.itemNumber} 失敗:`, error);
                  return { gift, matchedProduct: null };
                }
              })
            );
            setGiftMatches(matches);
          }
        } else {
          console.error("No such document!");
        }
      } catch (error) {
        console.error("Error fetching product: ", error);
      }
    };
    if (id) fetchProduct();
    return () => { active = false; };
  }, [country, id]);

  // 新增搜尋用的 state
  const [searchTerm, setSearchTerm] = useState('');

  // 新增搜尋相關的處理函數
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearchClick();
    }
  };

  const handleSearchClick = () => {
    const term = searchTerm.trim();
    if (term) {
      navigate(`/products/${country}?search=${encodeURIComponent(term)}`);
    }
  };

  const addPhotoDraftByUrl = () => {
    const url = newPhotoUrl.trim();
    if (!url) return;
    setPhotoDrafts(prev => buildProductPhotos({
      ...product,
      photos: [
        ...prev,
        { url, source: 'url', label: '商品照片', createdAt: new Date().toISOString() },
      ],
    }));
    setNewPhotoUrl('');
    setPhotoDirty(true);
  };

  const handlePhotoDraftUpload = async (e, replaceUrl = null) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;

    const context = `${country}/${id}`;
    setIsReadingPhotos(true); setPhotoError('');
    try {
    const uploadedPhotos = await Promise.all(files.map(async (file) => {
      const compressedImage = await compressImageFile(file);
      return {
        url: compressedImage,
        source: 'upload',
        label: '商品照片',
        createdAt: new Date().toISOString(),
      };
    }));

    if (photoContext.current !== context) return;
    setPhotoDrafts(prev => buildProductPhotos({
      ...product,
      photos: [...prev.filter(photo => photo.url !== replaceUrl || ['main', 'style'].includes(photo.source)), ...uploadedPhotos],
    }));
    setPhotoDirty(true);
    } catch (error) {
      if (photoContext.current === context) setPhotoError(`${error.message}；原有照片草稿仍保留，請重新選取照片。`);
    } finally { if (photoContext.current === context) setIsReadingPhotos(false); }
  };

  const removeDraftPhoto = (url) => {
    setPhotoDrafts(prev => prev.filter(photo => photo.url !== url));
    setPhotoDirty(true);
  };

  const savePhotoDrafts = async () => {
    if (isSavingPhotos || isReadingPhotos) return;
    setIsSavingPhotos(true);
    setPhotoError('');
    try {
      const base = photoBase || product;
      const manualPhotos = photoDrafts
        .filter(photo => photo.source !== 'style')
        .map(photo => ({
          ...(base.photos || []).find(original => original?.url === photo.url),
          ...photo,
          url: String(photo.url || ''),
          source: String(photo.source || 'url'),
          label: String(photo.label || '商品照片'),
          createdAt: String(photo.createdAt || new Date().toISOString()),
        }))
        .filter(photo => photo.url);
      const saved = await saveProductPatch({ country, id, base, patch: { photos: manualPhotos } });
      setProduct(saved);
      setPhotoDirty(false); setPhotoError('');
      setShowPhotoManager(false);
    } catch (error) {
      console.error('儲存商品相簿失敗:', error);
      setPhotoError(`儲存失敗：${error.message || '請稍後重試'}。照片草稿仍保留。`);
    } finally {
      setIsSavingPhotos(false);
    }
  };

  if (!product) {
    return (
      <p style={{ fontSize: '1.25rem', textAlign: 'center', padding: '1rem' }}>
        找不到該商品
      </p>
    );
  }

  const {
    image,
    name,
    itemNumber,
    livePrice,
    onlinePrice,
    remarks,
    costKoreanWon,
    costJPY,
    costTWD,
    costTHB,
    costRMB,
    weight,
    styles,
    history
  } = product;
  const productPhotos = buildProductPhotos(product);
  const isPreparationProduct = hasPreparationData(product);

  const renderPriceInfo = () => {
    if (isCrystal) return null;
    if (Number(livePrice) === Number(onlinePrice)) {
      return (
        <div style={{ padding: '1rem', background: '#fff5f5', borderRadius: '8px', border: '1px solid #ffcccc' }}>
          <span style={{ fontSize: '0.85rem', color: '#666', display: 'block', marginBottom: '0.5rem' }}>售價</span>
          <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#d32f2f' }}>NT$ {Math.ceil(Number(livePrice))}</div>
        </div>
      );
    } else {
      return (
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <div style={{ flex: 1, padding: '1rem', background: '#fff5f5', borderRadius: '8px', border: '1px solid #ffcccc' }}>
            <span style={{ fontSize: '0.85rem', color: '#666', display: 'block', marginBottom: '0.5rem' }}>直播價</span>
            <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#d32f2f' }}>NT$ {Math.ceil(Number(livePrice))}</div>
          </div>
          <div style={{ flex: 1, padding: '1rem', background: '#f0f9f4', borderRadius: '8px', border: '1px solid #cce5d6' }}>
            <span style={{ fontSize: '0.85rem', color: '#666', display: 'block', marginBottom: '0.5rem' }}>商城價</span>
            <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#2e7d32' }}>NT$ {Math.ceil(Number(onlinePrice))}</div>
          </div>
        </div>
      );
    }
  };

  const calculateExchangeCost = (cost) => cost / exchangeRate;
  // 計算組合商品的成本和重量總和
  const calculateComboProductCostWeight = () => {
    if (!product.subProducts || product.subProducts.length === 0 || !subProductMatches || subProductMatches.length === 0) {
      return null;
    }

    let totalCostKoreanWon = 0;
    let totalCostJPY = 0;
    let totalCostTWD = 0;
    let totalCostTHB = 0;
    let totalWeight = 0;
    let hasMissingData = false;

    subProductMatches.forEach((match) => {
      const { subProduct, matchedProduct } = match;
      if (!matchedProduct) {
        hasMissingData = true;
        return;
      }

      // 找到對應的樣式
      let styleIndex = -1;
      if (subProduct.style && subProduct.style !== '--') {
        // 有指定樣式，嘗試找到匹配的樣式
        styleIndex = matchedProduct.styles?.findIndex((s, i) => {
          const styleDetail = matchedProduct.styleDetails?.[i];
          return s === subProduct.style || (styleDetail && styleDetail.style === subProduct.style);
        });
      } else if (matchedProduct.styles && matchedProduct.styles.length > 0) {
        // 沒有指定樣式，使用第一個樣式
        styleIndex = 0;
      }

      // 獲取成本和重量（優先使用樣式級別的，否則使用商品級別的）
      const styleDetail = styleIndex >= 0 ? matchedProduct.styleDetails?.[styleIndex] : null;
      const subCostKoreanWon = styleDetail?.costKoreanWon || matchedProduct.costKoreanWon || '';
      const subCostJPY = styleDetail?.costJPY || matchedProduct.costJPY || '';
      const subCostTWD = styleDetail?.costTWD || matchedProduct.costTWD || '';
      const subCostTHB = styleDetail?.costTHB || matchedProduct.costTHB || '';
      const subWeight = styleDetail?.weight || matchedProduct.weight || '';

      // 檢查是否有缺失的數據
      if (!subCostKoreanWon && !subCostJPY && !subCostTWD && !subCostTHB) {
        hasMissingData = true;
      }
      if (!subWeight) {
        hasMissingData = true;
      }

      // 計算數量倍數
      const quantity = subProduct.quantity || 1;

      // 累加成本和重量
      if (subCostKoreanWon) totalCostKoreanWon += Number(subCostKoreanWon) * quantity;
      if (subCostJPY) totalCostJPY += Number(subCostJPY) * quantity;
      if (subCostTWD) totalCostTWD += Number(subCostTWD) * quantity;
      if (subCostTHB) totalCostTHB += Number(subCostTHB) * quantity;
      if (subWeight) totalWeight += Number(subWeight) * quantity;
    });

    if (hasMissingData) {
      return { hasMissingData: true };
    }

    return {
      costKoreanWon: totalCostKoreanWon || '',
      costJPY: totalCostJPY || '',
      costTWD: totalCostTWD || '',
      costTHB: totalCostTHB || '',
      weight: totalWeight || '',
      hasMissingData: false
    };
  };

  const calculateShippingCost = (freightSpec, useCostKoreanWon, useCostJPY, useCostTWD, useCostTHB, useCostRMB, useWeight) => {
    // 使用傳入的成本和重量，如果沒有則使用商品級別的值
    // 處理空字符串、null、undefined 的情況
    const finalCostKoreanWon = (useCostKoreanWon !== null && useCostKoreanWon !== undefined && useCostKoreanWon !== '') ? useCostKoreanWon : (costKoreanWon || '');
    const finalCostJPY = (useCostJPY !== null && useCostJPY !== undefined && useCostJPY !== '') ? useCostJPY : (costJPY || '');
    const finalCostTWD = (useCostTWD !== null && useCostTWD !== undefined && useCostTWD !== '') ? useCostTWD : (costTWD || '');
    const finalCostTHB = (useCostTHB !== null && useCostTHB !== undefined && useCostTHB !== '') ? useCostTHB : (costTHB || '');
    const finalCostRMB = (useCostRMB !== null && useCostRMB !== undefined && useCostRMB !== '') ? useCostRMB : (costRMB || '');
    const finalWeight = (useWeight !== null && useWeight !== undefined && useWeight !== '') ? useWeight : (weight || '');
    
    // 轉換為數字
    const numCostKoreanWon = finalCostKoreanWon ? Number(finalCostKoreanWon) : 0;
    const numCostJPY = finalCostJPY ? Number(finalCostJPY) : 0;
    const numCostTWD = finalCostTWD ? Number(finalCostTWD) : 0;
    const numCostTHB = finalCostTHB ? Number(finalCostTHB) : 0;
    const numCostRMB = finalCostRMB ? Number(finalCostRMB) : 0;
    const numWeight = finalWeight ? Number(finalWeight) : 0;
    
    // 確定使用哪個成本（優先順序：台幣 > 其他貨幣）
    let baseCostInTWD = 0;
    
    // 如果直接有台幣成本，使用台幣成本
    if (numCostTWD) {
      baseCostInTWD = numCostTWD;
    } else {
      // 否則根據國家選擇對應貨幣並轉換為台幣
      let cost;
      if (isChina) {
        cost = numCostRMB;
        if (cost) {
          // 中國：人民幣 * 匯率 = 台幣
          baseCostInTWD = cost * exchangeRate;
        }
      } else if (isKorea) {
        cost = numCostKoreanWon;
        if (cost) {
          // 韓國：韓幣 / 匯率 = 台幣
          baseCostInTWD = cost / exchangeRate;
        }
      } else if (isJapan) {
        cost = numCostJPY || numCostKoreanWon;
        if (cost) {
          // 日本：日幣或韓幣 / 匯率 = 台幣
          baseCostInTWD = cost / exchangeRate;
        }
      } else if (isThailand) {
        cost = numCostTHB;
        if (cost) {
          // 泰國：泰銖 / 匯率 = 台幣
          baseCostInTWD = cost / exchangeRate;
        }
      }
    }
    
    // 如果沒有成本，返回 null
    if (!baseCostInTWD) return null;
    
    // 商品成本先加額外%數，運費不加%數
    const costWithExtra = baseCostInTWD * (1 + extraPercentage / 100);
    let shipping = 0;
    if (
      isKorea &&
      freightSpec &&
      typeof freightSpec === 'object' &&
      'twPerKg' in freightSpec
    ) {
      const tw = Number(freightSpec.twPerKg) || 0;
      const krw = Number(freightSpec.intlKrwPerKg) || 0;
      shipping = numWeight * tw + (numWeight * krw) / exchangeRate;
    } else {
      const rate = Number(freightSpec) || 0;
      shipping = numWeight * rate;
    }
    const totalCost = costWithExtra + shipping;
    return isNaN(totalCost) ? null : Math.ceil(totalCost);
  };

  const renderCostInfo = () => {
    if (isCrystal) return null;
    if (isChina) {
      if (costRMB) {
        const convertedCost = Math.ceil(Number(costRMB * exchangeRate));
        const convertedCostWithExtra = Math.ceil(Number(costRMB * exchangeRate * (1 + extraPercentage / 100)));
        // 運費不加%數
        const shippingCost = Math.ceil(Number(weight * shippingRate * exchangeRate));
        const totalCost = convertedCostWithExtra + shippingCost;
        return (
          <>
            <p style={{ fontSize: '1.25rem', margin: '0.25rem 0' }}>
              人民幣成本: {Math.ceil(Number(costRMB))}
            </p>
            <p style={{ fontSize: '1.25rem', margin: '0.25rem 0' }}>
              台幣換算: {convertedCost} {targetCurrencyCode}
            </p>
            {extraPercentage > 0 && (
              <p style={{ fontSize: '1.25rem', margin: '0.25rem 0', color: '#2d5a87', fontWeight: 'bold' }}>
                台幣換算+額外{extraPercentage}%: {convertedCostWithExtra} {targetCurrencyCode}
              </p>
            )}
            <p style={{ fontSize: '1.25rem', margin: '0.25rem 0' }}>
              🚢海快成本: {shippingCost} {targetCurrencyCode}
            </p>
            <p style={{ fontSize: '1.25rem', margin: '0.25rem 0' }}>
              總成本: {totalCost} {targetCurrencyCode}
            </p>
          </>
        );
      }
    } else if (isKorea) {
      if (costKoreanWon) {
        const convertedCost = Math.ceil(Number(costKoreanWon / exchangeRate));
        const convertedCostWithExtra = Math.ceil(Number(costKoreanWon / exchangeRate * (1 + extraPercentage / 100)));
        return (
          <>
            <p style={{ fontSize: '1.25rem', margin: '0.25rem 0' }}>
              韓幣成本: {Math.ceil(Number(costKoreanWon))}
            </p>
            <p style={{ fontSize: '1.25rem', margin: '0.25rem 0' }}>
              台幣換算: {convertedCost} {targetCurrencyCode}
            </p>
            {extraPercentage > 0 && (
              <p style={{ fontSize: '1.25rem', margin: '0.25rem 0', color: '#2d5a87', fontWeight: 'bold' }}>
                台幣換算+額外{extraPercentage}%: {convertedCostWithExtra} {targetCurrencyCode}
              </p>
            )}
          </>
        );
      } else if (costTWD) {
        const costWithExtra = Math.ceil(Number(costTWD * (1 + extraPercentage / 100)));
        return (
          <>
            <p style={{ fontSize: '1.25rem', margin: '0.25rem 0' }}>
              台幣成本: {Math.ceil(Number(costTWD))}
            </p>
            {extraPercentage > 0 && (
              <p style={{ fontSize: '1.25rem', margin: '0.25rem 0', color: '#2d5a87', fontWeight: 'bold' }}>
                台幣成本+額外{extraPercentage}%: {costWithExtra} TWD
              </p>
            )}
          </>
        );
      }
    } else if (isJapan) {
      if (costJPY) {
        const convertedCost = Math.ceil(Number(costJPY / exchangeRate));
        const convertedCostWithExtra = Math.ceil(Number(costJPY / exchangeRate * (1 + extraPercentage / 100)));
        return (
          <>
            <p style={{ fontSize: '1.25rem', margin: '0.25rem 0' }}>
              日幣成本: {Math.ceil(Number(costJPY))}
            </p>
            <p style={{ fontSize: '1.25rem', margin: '0.25rem 0' }}>
              台幣換算: {convertedCost} {targetCurrencyCode}
            </p>
            {extraPercentage > 0 && (
              <p style={{ fontSize: '1.25rem', margin: '0.25rem 0', color: '#2d5a87', fontWeight: 'bold' }}>
                台幣換算+額外{extraPercentage}%: {convertedCostWithExtra} {targetCurrencyCode}
              </p>
            )}
          </>
        );
      } else if (costTWD) {
        const costWithExtra = Math.ceil(Number(costTWD * (1 + extraPercentage / 100)));
        return (
          <>
            <p style={{ fontSize: '1.25rem', margin: '0.25rem 0' }}>
              台幣成本: {Math.ceil(Number(costTWD))}
            </p>
            {extraPercentage > 0 && (
              <p style={{ fontSize: '1.25rem', margin: '0.25rem 0', color: '#2d5a87', fontWeight: 'bold' }}>
                台幣成本+額外{extraPercentage}%: {costWithExtra} TWD
              </p>
            )}
          </>
        );
      } else if (costKoreanWon) {
        const convertedCost = Math.ceil(Number(costKoreanWon / exchangeRate));
        const convertedCostWithExtra = Math.ceil(Number(costKoreanWon / exchangeRate * (1 + extraPercentage / 100)));
        return (
          <>
            <p style={{ fontSize: '1.25rem', margin: '0.25rem 0' }}>
              韓幣成本: {Math.ceil(Number(costKoreanWon))}
            </p>
            <p style={{ fontSize: '1.25rem', margin: '0.25rem 0' }}>
              台幣換算: {convertedCost} {targetCurrencyCode}
            </p>
            {extraPercentage > 0 && (
              <p style={{ fontSize: '1.25rem', margin: '0.25rem 0', color: '#2d5a87', fontWeight: 'bold' }}>
                台幣換算+額外{extraPercentage}%: {convertedCostWithExtra} {targetCurrencyCode}
              </p>
            )}
          </>
        );
      }
    } else if (isThailand) {
      if (costTHB) {
        const convertedCost = Math.ceil(Number(costTHB / exchangeRate));
        const convertedCostWithExtra = Math.ceil(Number(costTHB / exchangeRate * (1 + extraPercentage / 100)));
        return (
          <>
            <p style={{ fontSize: '1.25rem', margin: '0.25rem 0' }}>
              泰銖成本: {Math.ceil(Number(costTHB))}
            </p>
            <p style={{ fontSize: '1.25rem', margin: '0.25rem 0' }}>
              台幣換算: {convertedCost} {targetCurrencyCode}
            </p>
            {extraPercentage > 0 && (
              <p style={{ fontSize: '1.25rem', margin: '0.25rem 0', color: '#2d5a87', fontWeight: 'bold' }}>
                台幣換算+額外{extraPercentage}%: {convertedCostWithExtra} {targetCurrencyCode}
              </p>
            )}
          </>
        );
      } else if (costTWD) {
        const costWithExtra = Math.ceil(Number(costTWD * (1 + extraPercentage / 100)));
        return (
          <>
            <p style={{ fontSize: '1.25rem', margin: '0.25rem 0' }}>
              台幣成本: {Math.ceil(Number(costTWD))}
            </p>
            {extraPercentage > 0 && (
              <p style={{ fontSize: '1.25rem', margin: '0.25rem 0', color: '#2d5a87', fontWeight: 'bold' }}>
                台幣成本+額外{extraPercentage}%: {costWithExtra} TWD
              </p>
            )}
          </>
        );
      }
    }
    return null;
  };

  // 計算樣式成本信息（使用樣式的成本和重量）
  const renderStyleCostInfo = (styleCostKoreanWon, styleCostJPY, styleCostTWD, styleCostTHB, styleCostRMB, styleWeight) => {
    // 使用樣式的成本和重量，如果沒有則使用商品級別的值
    const useCostKoreanWon = styleCostKoreanWon !== null && styleCostKoreanWon !== undefined && styleCostKoreanWon !== '' ? styleCostKoreanWon : costKoreanWon;
    const useCostJPY = styleCostJPY !== null && styleCostJPY !== undefined && styleCostJPY !== '' ? styleCostJPY : costJPY;
    const useCostTWD = styleCostTWD !== null && styleCostTWD !== undefined && styleCostTWD !== '' ? styleCostTWD : costTWD;
    const useCostTHB = styleCostTHB !== null && styleCostTHB !== undefined && styleCostTHB !== '' ? styleCostTHB : costTHB;
    const useCostRMB = styleCostRMB !== null && styleCostRMB !== undefined && styleCostRMB !== '' ? styleCostRMB : costRMB;
    const useWeight = styleWeight !== null && styleWeight !== undefined && styleWeight !== '' ? styleWeight : weight;

    if (isChina) {
      if (useCostRMB) {
        const convertedCost = Math.ceil(Number(useCostRMB * exchangeRate));
        const convertedCostWithExtra = Math.ceil(Number(useCostRMB * exchangeRate * (1 + extraPercentage / 100)));
        const shippingCost = Math.ceil(Number(useWeight * shippingRate * exchangeRate));
        const totalCost = convertedCostWithExtra + shippingCost;
        return (
          <div style={{ fontSize: '1rem', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e5e5' }}>
            {/* 第一行：成本 重量 */}
            <div style={{ marginBottom: '0.75rem', lineHeight: '1.6' }}>
              <span style={{ marginRight: '1.5rem', fontSize: '1.1rem' }}>成本: {Math.ceil(Number(useCostRMB))} RMB</span>
              <span style={{ fontSize: '1.1rem' }}>重量: {useWeight || '未設定'} kg</span>
            </div>
            {/* 第二行：台幣換算 */}
            <div style={{ marginBottom: '0.5rem', lineHeight: '1.6' }}>
              <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#000' }}>
                台幣換算: {convertedCost} {targetCurrencyCode}
              </span>
            </div>
            {/* 第三行：台幣換算+額外10% */}
            {extraPercentage > 0 && (
              <div style={{ lineHeight: '1.6' }}>
                <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#2d5a87' }}>
                  台幣換算+額外{extraPercentage}%: {convertedCostWithExtra} {targetCurrencyCode}
                </span>
              </div>
            )}
            <div style={{ marginTop: '0.5rem', fontSize: '0.95rem', color: '#666' }}>
              <span style={{ marginRight: '1rem' }}>海快成本: {shippingCost} {targetCurrencyCode}</span>
              <span style={{ fontWeight: 'bold', color: '#2d5a87' }}>總成本: {totalCost} {targetCurrencyCode}</span>
            </div>
          </div>
        );
      }
    } else if (isKorea) {
      if (useCostKoreanWon) {
        const convertedCost = Math.ceil(Number(useCostKoreanWon / exchangeRate));
        const convertedCostWithExtra = Math.ceil(Number(useCostKoreanWon / exchangeRate * (1 + extraPercentage / 100)));
        return (
          <div style={{ fontSize: '1rem', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e5e5' }}>
            {/* 第一行：成本 重量 */}
            <div style={{ marginBottom: '0.75rem', lineHeight: '1.6' }}>
              <span style={{ marginRight: '1.5rem', fontSize: '1.1rem' }}>成本: {Math.ceil(Number(useCostKoreanWon))} KRW</span>
              <span style={{ fontSize: '1.1rem' }}>重量: {useWeight || '未設定'} kg</span>
            </div>
            {/* 第二行：台幣換算 */}
            <div style={{ marginBottom: '0.5rem', lineHeight: '1.6' }}>
              <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#000' }}>
                台幣換算: {convertedCost} {targetCurrencyCode}
              </span>
            </div>
            {/* 第三行：台幣換算+額外10% */}
            {extraPercentage > 0 && (
              <div style={{ lineHeight: '1.6' }}>
                <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#2d5a87' }}>
                  台幣換算+額外{extraPercentage}%: {convertedCostWithExtra} {targetCurrencyCode}
                </span>
              </div>
            )}
          </div>
        );
      } else if (useCostTWD) {
        const costWithExtra = Math.ceil(Number(useCostTWD * (1 + extraPercentage / 100)));
        return (
          <div style={{ fontSize: '1rem', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e5e5' }}>
            {/* 第一行：成本 重量 */}
            <div style={{ marginBottom: '0.75rem', lineHeight: '1.6' }}>
              <span style={{ marginRight: '1.5rem', fontSize: '1.1rem' }}>成本: {Math.ceil(Number(useCostTWD))} {targetCurrencyCode}</span>
              <span style={{ fontSize: '1.1rem' }}>重量: {useWeight || '未設定'} kg</span>
            </div>
            {/* 第二行：台幣換算+額外10% */}
            {extraPercentage > 0 && (
              <div style={{ lineHeight: '1.6' }}>
                <span style={{ fontSize: '1.3rem', fontWeight: '600', color: '#2d5a87' }}>
                  成本+額外{extraPercentage}%: {costWithExtra} {targetCurrencyCode}
                </span>
              </div>
            )}
          </div>
        );
      }
    } else if (isJapan) {
      if (useCostJPY) {
        const convertedCost = Math.ceil(Number(useCostJPY / exchangeRate));
        const convertedCostWithExtra = Math.ceil(Number(useCostJPY / exchangeRate * (1 + extraPercentage / 100)));
        return (
          <div style={{ fontSize: '1rem', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e5e5' }}>
            {/* 第一行：成本 重量 */}
            <div style={{ marginBottom: '0.75rem', lineHeight: '1.6' }}>
              <span style={{ marginRight: '1.5rem', fontSize: '1.1rem' }}>成本: {Math.ceil(Number(useCostJPY))} JPY</span>
              <span style={{ fontSize: '1.1rem' }}>重量: {useWeight || '未設定'} kg</span>
            </div>
            {/* 第二行：台幣換算 */}
            <div style={{ marginBottom: '0.5rem', lineHeight: '1.6' }}>
              <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#000' }}>
                台幣換算: {convertedCost} {targetCurrencyCode}
              </span>
            </div>
            {/* 第三行：台幣換算+額外10% */}
            {extraPercentage > 0 && (
              <div style={{ lineHeight: '1.6' }}>
                <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#2d5a87' }}>
                  台幣換算+額外{extraPercentage}%: {convertedCostWithExtra} {targetCurrencyCode}
                </span>
              </div>
            )}
          </div>
        );
      } else if (useCostTWD) {
        const costWithExtra = Math.ceil(Number(useCostTWD * (1 + extraPercentage / 100)));
        return (
          <div style={{ fontSize: '1rem', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e5e5' }}>
            {/* 第一行：成本 重量 */}
            <div style={{ marginBottom: '0.75rem', lineHeight: '1.6' }}>
              <span style={{ marginRight: '1.5rem', fontSize: '1.1rem' }}>成本: {Math.ceil(Number(useCostTWD))} {targetCurrencyCode}</span>
              <span style={{ fontSize: '1.1rem' }}>重量: {useWeight || '未設定'} kg</span>
            </div>
            {/* 第二行：成本+額外10% */}
            {extraPercentage > 0 && (
              <div style={{ lineHeight: '1.6' }}>
                <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#2d5a87' }}>
                  成本+額外{extraPercentage}%: {costWithExtra} {targetCurrencyCode}
                </span>
              </div>
            )}
          </div>
        );
      }
    } else if (isThailand) {
      if (useCostTHB) {
        const convertedCost = Math.ceil(Number(useCostTHB / exchangeRate));
        const convertedCostWithExtra = Math.ceil(Number(useCostTHB / exchangeRate * (1 + extraPercentage / 100)));
        return (
          <div style={{ fontSize: '1rem', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e5e5' }}>
            {/* 第一行：成本 重量 */}
            <div style={{ marginBottom: '0.75rem', lineHeight: '1.6' }}>
              <span style={{ marginRight: '1.5rem', fontSize: '1.1rem' }}>成本: {Math.ceil(Number(useCostTHB))} THB</span>
              <span style={{ fontSize: '1.1rem' }}>重量: {useWeight || '未設定'} kg</span>
            </div>
            {/* 第二行：台幣換算 */}
            <div style={{ marginBottom: '0.5rem', lineHeight: '1.6' }}>
              <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#000' }}>
                台幣換算: {convertedCost} {targetCurrencyCode}
              </span>
            </div>
            {/* 第三行：台幣換算+額外10% */}
            {extraPercentage > 0 && (
              <div style={{ lineHeight: '1.6' }}>
                <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#2d5a87' }}>
                  台幣換算+額外{extraPercentage}%: {convertedCostWithExtra} {targetCurrencyCode}
                </span>
              </div>
            )}
          </div>
        );
      } else if (useCostTWD) {
        const costWithExtra = Math.ceil(Number(useCostTWD * (1 + extraPercentage / 100)));
        return (
          <div style={{ fontSize: '1rem', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e5e5' }}>
            {/* 第一行：成本 重量 */}
            <div style={{ marginBottom: '0.75rem', lineHeight: '1.6' }}>
              <span style={{ marginRight: '1.5rem', fontSize: '1.1rem' }}>成本: {Math.ceil(Number(useCostTWD))} {targetCurrencyCode}</span>
              <span style={{ fontSize: '1.1rem' }}>重量: {useWeight || '未設定'} kg</span>
            </div>
            {/* 第二行：成本+額外10% */}
            {extraPercentage > 0 && (
              <div style={{ lineHeight: '1.6' }}>
                <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#2d5a87' }}>
                  成本+額外{extraPercentage}%: {costWithExtra} {targetCurrencyCode}
                </span>
              </div>
            )}
          </div>
        );
      }
    }
    return null;
  };

  const renderStyles = () => {
    const activeVariants = getProductVariants(product).filter(variant => variant.active);
    if (!activeVariants.length) return <p style={{ fontSize: '1.25rem' }}>無啟用樣式</p>;
    const priceMap = new Map();
    activeVariants.forEach(variant => {
      const styleDetail = product.styleDetails?.[variant.index];
      const styleLivePrice = styleDetail?.livePrice === 0 ? 0 : styleDetail?.livePrice || livePrice || '';
      const styleOnlinePrice = styleDetail?.onlinePrice === 0 ? 0 : styleDetail?.onlinePrice || onlinePrice || '';
      const priceKey = JSON.stringify({ livePrice: styleLivePrice, onlinePrice: styleOnlinePrice });
      if (!priceMap.has(priceKey)) priceMap.set(priceKey, []);
      priceMap.get(priceKey).push({
        id: variant.id,
        styleText: variant.name,
        styleDetail,
        displayBarcode: variant.rawBarcode || extractRawBarcode(variant.barcode, itemNumber),
      });
    });

    return (
      <div>
                {Array.from(priceMap.entries()).map(([priceKey, styleGroup], groupIdx) => {
          const firstStyle = styleGroup[0];
          const styleDetail = firstStyle.styleDetail;
          
          // 樣式級別的價格（同一組的樣式應該有相同的價格）
          const styleLivePrice = firstStyle.styleDetail?.livePrice;
          const styleOnlinePrice = firstStyle.styleDetail?.onlinePrice;
          
          return (
            <div key={groupIdx} style={{ fontSize: '1.25rem', marginBottom: '1rem', padding: '0.75rem', border: '1px solid #e5e5e5', borderRadius: '8px', backgroundColor: '#fafafa' }}>
              {/* 樣式列表 */}
              <div style={{ marginBottom: (styleLivePrice || styleOnlinePrice) ? '0.75rem' : '0' }}>
                {styleGroup.map((styleItem, itemIdx) => {
                  // 只顯示樣式專屬的圖片，如果沒有就不顯示
                  const styleImage = styleItem.styleDetail?.image;
                  return (
                    <div key={styleItem.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: itemIdx < styleGroup.length - 1 ? '0.75rem' : '0', paddingBottom: itemIdx < styleGroup.length - 1 ? '0.75rem' : '0', borderBottom: itemIdx < styleGroup.length - 1 ? '1px solid #e5e5e5' : 'none' }}>
                      {/* 樣式圖片：只有當樣式有專屬圖片時才顯示 */}
                      {styleImage && (
                        <img
                          src={styleImage}
                          alt={styleItem.styleText}
                          style={{
                            width: '60px',
                            height: '60px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                            border: '1px solid #ddd',
                            flexShrink: 0
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                      <div style={{ flex: 1 }}>
                        <div style={{ marginBottom: '0.25rem' }}>
                          <span style={{ fontWeight: '500', fontSize: '1.1rem' }}>{styleItem.styleText}</span>
                        </div>
                        {styleItem.displayBarcode && (
                          <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.25rem' }}>
                            條碼: {styleItem.displayBarcode}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* 樣式級別的價格顯示 */}
              {(styleLivePrice || styleOnlinePrice) && (
                <div style={{ 
                  padding: '0.75rem', 
                  background: '#f8f9fa', 
                  borderRadius: '6px',
                  border: '1px solid #e5e5e5'
                }}>
                  {Number(styleLivePrice) === Number(styleOnlinePrice) ? (
                    <div style={{ fontSize: '1.1rem', fontWeight: '600', color: '#333' }}>
                      售價: NT$ {Math.ceil(Number(styleLivePrice || styleOnlinePrice))}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                      {styleLivePrice && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ fontSize: '0.85rem', color: '#666' }}>直播價:</span>
                          <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#d32f2f' }}>
                            NT$ {Math.ceil(Number(styleLivePrice))}
                          </span>
                        </div>
                      )}
                      {styleOnlinePrice && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ fontSize: '0.85rem', color: '#666' }}>商城價:</span>
                          <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#2e7d32' }}>
                            NT$ {Math.ceil(Number(styleOnlinePrice))}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderHistoryEntry = (entry) => {
    // 如果是樣式級別的歷史記錄
    if (entry.style !== undefined) {
      const parts = [];
      parts.push(`日期: ${entry.date}`);
      parts.push(`樣式: ${entry.style}`);
      
      if (entry.before || entry.after) {
        const changes = [];
        
        // 檢查價格變更（圖片變更不顯示在歷史價格中）
        if (entry.before?.livePrice !== entry.after?.livePrice) {
          if (entry.after?.livePrice) {
            changes.push(`直播價: ${entry.before?.livePrice || '無'} → ${entry.after.livePrice}`);
          } else if (entry.before?.livePrice) {
            changes.push(`直播價: ${entry.before.livePrice} → 無`);
          }
        }
        
        if (entry.before?.onlinePrice !== entry.after?.onlinePrice) {
          if (entry.after?.onlinePrice) {
            changes.push(`商城價: ${entry.before?.onlinePrice || '無'} → ${entry.after.onlinePrice}`);
          } else if (entry.before?.onlinePrice) {
            changes.push(`商城價: ${entry.before.onlinePrice} → 無`);
          }
        }
        
        // 檢查成本變更
        if (entry.before?.costKoreanWon !== entry.after?.costKoreanWon) {
          if (entry.after?.costKoreanWon) {
            changes.push(`成本韓幣: ${entry.before?.costKoreanWon || '無'} → ${entry.after.costKoreanWon}`);
          } else if (entry.before?.costKoreanWon) {
            changes.push(`成本韓幣: ${entry.before.costKoreanWon} → 無`);
          }
        }
        
        if (entry.before?.costJPY !== entry.after?.costJPY) {
          if (entry.after?.costJPY) {
            changes.push(`成本日幣: ${entry.before?.costJPY || '無'} → ${entry.after.costJPY}`);
          } else if (entry.before?.costJPY) {
            changes.push(`成本日幣: ${entry.before.costJPY} → 無`);
          }
        }
        
        if (entry.before?.costTWD !== entry.after?.costTWD) {
          if (entry.after?.costTWD) {
            changes.push(`成本台幣: ${entry.before?.costTWD || '無'} → ${entry.after.costTWD}`);
          } else if (entry.before?.costTWD) {
            changes.push(`成本台幣: ${entry.before.costTWD} → 無`);
          }
        }
        
        if (entry.before?.costTHB !== entry.after?.costTHB) {
          if (entry.after?.costTHB) {
            changes.push(`成本泰銖: ${entry.before?.costTHB || '無'} → ${entry.after.costTHB}`);
          } else if (entry.before?.costTHB) {
            changes.push(`成本泰銖: ${entry.before.costTHB} → 無`);
          }
        }
        
        // 檢查重量變更
        if (entry.before?.weight !== entry.after?.weight) {
          if (entry.after?.weight) {
            changes.push(`重量: ${entry.before?.weight || '無'} → ${entry.after.weight}`);
          } else if (entry.before?.weight) {
            changes.push(`重量: ${entry.before.weight} → 無`);
          }
        }
        
        if (changes.length > 0) {
          parts.push(`變更: ${changes.join(' | ')}`);
        }
        // 如果沒有變更，不顯示這條記錄（在過濾階段已經處理）
      }
      
      return parts.join(' | ');
    }
    
    // 商品級別的歷史記錄（原有邏輯）
    const parts = [];
    parts.push(`日期: ${entry.date}`);
    if (entry.costKoreanWon) parts.push(`韓幣成本: ${entry.costKoreanWon}`);
    if (entry.costJPY) parts.push(`日幣成本: ${entry.costJPY}`);
    if (entry.costTWD) parts.push(`台幣成本: ${entry.costTWD}`);
    if (entry.costTHB) parts.push(`泰銖成本: ${entry.costTHB}`);
    if (entry.costRMB) parts.push(`人民幣成本: ${entry.costRMB}`);
    if (entry.livePrice) parts.push(`直播價: ${entry.livePrice}`);
    if (entry.onlinePrice) parts.push(`線上價: ${entry.onlinePrice}`);
    if (entry.remarks) parts.push(`備註: ${entry.remarks}`);
    if (entry.vendor) parts.push(`廠商: ${entry.vendor}`);
    if (entry.action) parts.push(`操作: ${entry.action}`);
    if (entry.mergedProductId) parts.push(`合併商品ID: ${entry.mergedProductId}`);
    if (entry.itemNumber) parts.push(`料號: ${entry.itemNumber}`);
    // 顯示子商品和贈品變更
    if (entry.subProducts !== undefined) {
      if (entry.subProducts && entry.subProducts.length > 0) {
        const subProductStr = entry.subProducts.map(sp => `${sp.itemNumber}(${sp.style || '--'})*${sp.quantity}`).join(', ');
        parts.push(`子商品: ${subProductStr}`);
      } else {
        parts.push(`子商品: 已移除`);
      }
    }
    if (entry.gifts !== undefined) {
      if (entry.gifts && entry.gifts.length > 0) {
        const giftStr = entry.gifts.map(g => `${g.title ? `【${g.title}】` : ''}${g.itemNumber}(${g.style || '--'})*${g.quantity}`).join(', ');
        parts.push(`贈品: ${giftStr}`);
      } else {
        parts.push(`贈品: 已移除`);
      }
    }
    return parts.join(' | ');
  };

  const renderHistoryList = () => {
    if (!history || history.length === 0) {
      return (
        <div style={{ padding: '1rem', textAlign: 'center', color: '#666', fontSize: '0.9rem' }}>
          尚無歷史記錄
        </div>
      );
    }
    
    // 過濾歷史記錄：移除圖片變更記錄、無變更記錄和重複記錄
    const filteredHistory = [];
    const seenEntries = new Set();
    
    history.forEach(entry => {
      // 跳過樣式級別但沒有實際變更的記錄
      if (entry.style !== undefined) {
        // 如果沒有 before 和 after，跳過
        if (!entry.before && !entry.after) {
          return;
        }
        
        // 如果有 before 和 after，檢查是否有實際變更
        if (entry.before && entry.after) {
          // 檢查是否只有圖片變更（沒有其他變更）
          const beforeWithoutImage = { ...entry.before };
          delete beforeWithoutImage.image;
          const afterWithoutImage = { ...entry.after };
          delete afterWithoutImage.image;
          
          // 如果變更前後完全相同（除了圖片），跳過
          if (JSON.stringify(beforeWithoutImage) === JSON.stringify(afterWithoutImage)) {
            return;
          }
        } else if (entry.before === null && entry.after) {
          // 新增樣式：檢查 after 是否有實際內容（不包含圖片）
          const afterWithoutImage = { ...entry.after };
          delete afterWithoutImage.image;
          const hasContent = Object.values(afterWithoutImage).some(v => v !== null && v !== undefined && v !== '');
          if (!hasContent) {
            return;
          }
        } else if (entry.before && entry.after === null) {
          // 刪除樣式：保留記錄
        } else {
          // 其他情況：沒有實際變更，跳過
          return;
        }
      }
      
      // 生成記錄的唯一標識（用於去重，不包含圖片）
      const entryKey = JSON.stringify({
        date: entry.date,
        style: entry.style,
        action: entry.action,
        costKoreanWon: entry.costKoreanWon,
        costJPY: entry.costJPY,
        costTWD: entry.costTWD,
        costTHB: entry.costTHB,
        costRMB: entry.costRMB,
        livePrice: entry.livePrice,
        onlinePrice: entry.onlinePrice,
        remarks: entry.remarks,
        vendor: entry.vendor,
        // 對於樣式級別的記錄，比較變更內容（不包含圖片）
        before: entry.before ? (() => {
          const b = { ...entry.before };
          delete b.image;
          return b;
        })() : undefined,
        after: entry.after ? (() => {
          const a = { ...entry.after };
          delete a.image;
          return a;
        })() : undefined,
        subProducts: entry.subProducts,
        gifts: entry.gifts
      });
      
      // 如果已經見過相同的記錄，跳過
      if (!seenEntries.has(entryKey)) {
        seenEntries.add(entryKey);
        filteredHistory.push(entry);
      }
    });
    
    const sortedHistory = [...filteredHistory].reverse();
    let lastEntryText = '';
    return (
      <ul style={{ listStyle: 'none', padding: 0 }}>
        {sortedHistory.map((entry, index) => {
          const entryText = renderHistoryEntry(entry);
          // 跳過「無變更」的記錄
          if (entryText.includes('無變更')) return null;
          // 跳過只有日期和樣式名稱，沒有變更內容的記錄
          if (entry.style !== undefined && !entryText.includes('變更:')) return null;
          // 如果與上一條記錄相同，則跳過
          if (entryText === lastEntryText) return null;
          lastEntryText = entryText;
          return (
            <li 
              key={index} 
              style={{ 
                marginBottom: '0.75rem', 
                padding: '0.75rem', 
                border: '1px solid #e5e5e5', 
                borderRadius: '8px', 
                background: '#fafafa',
                fontSize: '0.9rem',
                lineHeight: '1.6'
              }}
            >
              <div style={{ color: '#333', wordBreak: 'break-word' }}>
                {entryText}
              </div>
            </li>
          );
        })}
      </ul>
    );
  };

  // 內嵌編輯廠商：直接以輸入框搭配 datalist 呈現
  const handleVendorSave = async () => {
    if (isSavingVendor) return;
    setIsSavingVendor(true);
    try {
      const saved = await saveProductPatch({ country, id, base: vendorBase || product, patch: { vendor: vendorInput } });
      setProduct(saved);
      localStorage.setItem('lastSelectedVendor', vendorInput.trim());
      setEditingVendor(false);
    } catch (error) {
      console.error('更新廠商失敗', error);
      alert(`更新廠商失敗：${error.message || '未知錯誤'}\n您的輸入仍保留。`);
    } finally {
      setIsSavingVendor(false);
    }
  };

  // 生成4位數驗證碼
  const generateVerificationCode = () => {
    return Math.floor(1000 + Math.random() * 9000).toString();
  };

  // 打開刪除確認對話框
  const handleOpenDeleteConfirm = () => {
    if (isPreparationProduct) { alert(PREPARATION_DELETE_MESSAGE); return; }
    const code = generateVerificationCode();
    setDeleteVerificationCode(code);
    setDeleteCodeInput('');
    setShowDeleteConfirm(true);
  };

  // 關閉刪除確認對話框
  const handleCloseDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    setDeleteCodeInput('');
    setDeleteVerificationCode('');
  };

  // The transaction validates the entire legacy/merged group before deleting any document.
  const handleDeleteProduct = async () => {
    if (isDeleting) return;
    if (isPreparationProduct) { alert(PREPARATION_DELETE_MESSAGE); return; }
    if (deleteCodeInput !== deleteVerificationCode) {
      alert('驗證碼錯誤，請重新輸入！');
      setDeleteVerificationCode(generateVerificationCode());
      setDeleteCodeInput('');
      return;
    }

    setIsDeleting(true);
    try {
      const collectionName = getCollectionName(country);
      const productIds = [...new Set([id, ...(Array.isArray(history) ? history : [])
        .map(entry => entry.mergedProductId)
        .filter(mergedId => typeof mergedId === 'string' && mergedId)])];
      await deleteLegacyProductsSafely(productIds.map(productId => doc(db, collectionName, productId)));
      // Keep audit entries. Rewriting other products' full history arrays can lose concurrent edits.
      alert('舊版商品刪除已完成；歷史紀錄保留。');
      navigate(`/products/${country}`);
    } catch (error) {
      console.error('刪除商品失敗', error);
      const message = error.code === 'legacy-product-protected'
        ? (error.message || PREPARATION_DELETE_MESSAGE)
        : `刪除未完成：${error.message || '請稍後再試'}`;
      alert(`${message}\n${error.code === 'legacy-product-protected' ? '本次未刪除任何商品。' : '請重新整理確認刪除結果後再重試。'}`);
      handleCloseDeleteConfirm();
    } finally {
      setIsDeleting(false);
    }
  };

  const pageStyle = {
    minHeight: '100vh',
    background: '#f8f9fa',
    padding: '1rem',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  };

  const cardStyle = {
    background: '#fff',
    borderRadius: '12px',
    padding: '1.5rem',
    marginBottom: '1rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
  };

  const sectionStyle = {
    marginBottom: '1.5rem',
    paddingTop: '1.5rem',
    borderTop: '1px solid #e5e5e5'
  };

  const labelStyle = {
    fontSize: '0.85rem',
    fontWeight: '500',
    color: '#666',
    marginBottom: '0.5rem',
    display: 'block'
  };

  const valueStyle = {
    fontSize: '1.1rem',
    color: '#333',
    fontWeight: '500'
  };

  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: '480px', margin: '0 auto' }}>
        <div style={cardStyle}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', textAlign: 'center', marginBottom: '1.5rem', color: '#333' }}>商品詳情</h2>
          {/* 商品相簿 */}
          {productPhotos.length > 0 ? (
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <img
                  src={selectedPhoto || productPhotos[0].url}
                  alt={name}
                  style={{ width: '100%', maxWidth: '320px', aspectRatio: '1 / 1', objectFit: 'cover', borderRadius: '16px', boxShadow: '0 10px 28px rgba(15,23,42,0.14)' }}
                />
              </div>
              {productPhotos.length > 1 && (
                <div style={{ display: 'flex', gap: '0.5rem', overflowX: 'auto', marginTop: '0.85rem', paddingBottom: '0.25rem' }}>
                  {productPhotos.map((photo) => (
                    <button
                      type="button"
                      key={photo.url}
                      onClick={() => setSelectedPhoto(photo.url)}
                      style={{
                        border: (selectedPhoto || productPhotos[0].url) === photo.url ? '2px solid #4f46e5' : '1px solid #e5e7eb',
                        borderRadius: '12px',
                        padding: '0.2rem',
                        background: '#fff',
                        cursor: 'pointer',
                        flex: '0 0 auto',
                      }}
                    >
                      <img src={photo.url} alt={photo.label || '商品照片'} style={{ width: '58px', height: '58px', objectFit: 'cover', borderRadius: '9px', display: 'block' }} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
              <div style={{ width: '100%', maxWidth: '320px', aspectRatio: '1 / 1', borderRadius: '16px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                無商品照片
              </div>
            </div>
          )}

          <ProductPrepMemberships country={country} product={{ ...product, id }} />
          {/* 商品名稱與價格 */}
          <div style={sectionStyle}>
            <h3 style={{ fontSize: '1.3rem', fontWeight: '600', marginBottom: '1rem', color: '#333' }}>
              {product.subProducts && product.subProducts.length > 0 && (
                <span style={{
                  display: 'inline-block',
                  padding: '0.10125rem 0.30375rem',
                  backgroundColor: '#d32f2f',
                  color: '#fff',
                  borderRadius: '4px',
                  fontSize: '1.053rem',
                  fontWeight: '600',
                  whiteSpace: 'nowrap',
                  lineHeight: '1.053rem',
                  marginRight: '0.5rem',
                  verticalAlign: 'baseline'
                }}>
                  組合
                </span>
              )}
              {name && name.startsWith('(組)') ? name.replace(/^\(組\)/, '') : name}
            </h3>
            {/* 檢查是否有樣式級別的價格，如果有則隱藏商品級別的價格 */}
            {(() => {
              const hasStylePrices = product.styleDetails && product.styleDetails.some(detail => detail && detail.active !== false && (detail.livePrice || detail.onlinePrice));
              // 如果沒有樣式級別的價格，才顯示商品級別的價格
              if (!hasStylePrices) {
                return renderPriceInfo();
              }
              return null;
            })()}
            <ProductEffects product={product} />
            {remarks && (
              <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#f8f9fa', borderRadius: '8px' }}>
                <span style={labelStyle}>備註</span>
                <div style={valueStyle}>{remarks}</div>
              </div>
            )}

            {/* 子商品資訊 */}
            {product.subProducts && product.subProducts.length > 0 && (
              <div style={{ marginTop: '0.75rem' }}>
                <span style={{ ...labelStyle, fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>子商品</span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {subProductMatches.map((match, idx) => {
                    const { subProduct, matchedProduct, matchedProductId } = match;
                    
                  if (matchedProduct) {
                    // 找到匹配的樣式，如果沒有指定樣式就隨機選擇一個
                    let styleIndex = -1;
                    let displayStyle = subProduct.style || '';
                    
                    if (subProduct.style) {
                      // 有指定樣式，嘗試找到匹配的樣式
                      styleIndex = matchedProduct.styles?.findIndex((s, i) => {
                        const styleDetail = matchedProduct.styleDetails?.[i];
                        return s === subProduct.style || (styleDetail && styleDetail.style === subProduct.style);
                      });
                    }
                    
                    // 如果沒有找到匹配的樣式或沒有指定樣式，隨機選擇一個
                    if (styleIndex < 0 && matchedProduct.styles && matchedProduct.styles.length > 0) {
                      // 使用料號和索引作為種子，確保相同子商品總是選擇相同的樣式
                      const seed = subProduct.itemNumber.charCodeAt(0) + idx;
                      styleIndex = seed % matchedProduct.styles.length;
                      displayStyle = matchedProduct.styles[styleIndex];
                    }
                    
                    const styleDetail = styleIndex >= 0 ? matchedProduct.styleDetails?.[styleIndex] : null;
                    const styleImage = styleDetail?.image || matchedProduct.image || '';
                    
                    return (
                      <div key={idx} style={{ padding: '0.75rem', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e5e5e5' }}>
                        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                          {styleImage && (
                            <img
                              src={styleImage}
                              alt={matchedProduct.name}
                              style={{
                                width: '60px',
                                height: '60px',
                                objectFit: 'cover',
                                borderRadius: '4px',
                                border: '1px solid #ddd',
                                flexShrink: 0
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          )}
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '500', fontSize: '1rem', marginBottom: '0.25rem' }}>
                              {matchedProduct.name}
                            </div>
                            <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.25rem' }}>
                              料號: {subProduct.itemNumber}
                            </div>
                            {displayStyle && (
                              <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.25rem' }}>
                                樣式: {displayStyle}
                              </div>
                            )}
                            <div style={{ fontSize: '0.9rem', color: '#333', fontWeight: '500', marginBottom: '0.25rem' }}>
                              數量: {subProduct.quantity}
                            </div>
                            {matchedProductId && (
                              <button
                                onClick={() => window.open(`https://skybluelive.com/view/${country}/${matchedProductId}`, '_blank')}
                                style={{
                                  marginTop: '0.25rem',
                                  padding: '0.25rem 0.75rem',
                                  fontSize: '0.85rem',
                                  background: '#fff',
                                  border: '1px solid #666',
                                  borderRadius: '4px',
                                  color: '#666',
                                  cursor: 'pointer'
                                }}
                              >
                                查看
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  } else {
                    // 沒有匹配到商品，用文字顯示
                    return (
                      <div key={idx} style={{ padding: '0.75rem', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e5e5e5' }}>
                        <div style={{ fontSize: '0.9rem', color: '#666' }}>
                          料號: {subProduct.itemNumber}
                          {subProduct.style && ` (樣式: ${subProduct.style})`}
                          {' '}× {subProduct.quantity}
                        </div>
                      </div>
                    );
                  }
                  })}
                </div>
              </div>
            )}

            {/* 贈品資訊 */}
            {product.gifts && product.gifts.length > 0 && (() => {
              // 檢查是否有模式標識（多層贈品）
              const hasMode = product.gifts.some(g => g.mode);
              const mode = hasMode ? product.gifts.find(g => g.mode)?.mode : null;
              
              // 按標題分組贈品（相同條件的贈品放在一起）
              const groupedGifts = {};
              giftMatches.forEach((match, idx) => {
                const { gift, matchedProduct, matchedProductId } = match;
                // 使用標題作為分組鍵，相同標題的贈品放在同一組
                const key = gift.title || '';
                if (!groupedGifts[key]) {
                  groupedGifts[key] = {
                    title: gift.title,
                    matchedProduct,
                    matchedProductId,
                    gifts: []
                  };
                }
                groupedGifts[key].gifts.push(gift);
              });

              return (
                <div style={{ marginTop: '0.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                    <span style={{ ...labelStyle, fontSize: '1rem', fontWeight: '600' }}>贈品</span>
                    {mode && (
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '0.4rem 0.9rem',
                        backgroundColor: mode === '由高至低' ? '#fff3e0' : mode === '只取最高' ? '#f3e5f5' : '#e8f5e9',
                        border: `2px solid ${mode === '由高至低' ? '#ff9800' : mode === '只取最高' ? '#9c27b0' : '#4caf50'}`,
                        borderRadius: '6px',
                        fontSize: '0.85rem',
                        color: mode === '由高至低' ? '#e65100' : mode === '只取最高' ? '#6a1b9a' : '#2e7d32',
                        fontWeight: '600',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        marginLeft: '0.5rem'
                      }}>
                        <span style={{ 
                          display: 'inline-block',
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          backgroundColor: mode === '由高至低' ? '#ff9800' : mode === '只取最高' ? '#9c27b0' : '#4caf50',
                          marginRight: '0.5rem'
                        }}></span>
                        {mode === '由高至低' && '由高至低'}
                        {mode === '只取最高' && '只取最高'}
                        {mode === '各自計算' && '各自計算'}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {Object.values(groupedGifts).map((group, groupIdx) => {
                      const { title, matchedProduct, matchedProductId, gifts } = group;
                      // 獲取第一個贈品的料號用於顯示
                      const itemNumber = gifts[0]?.itemNumber || '';
                      
                      // 判斷是「各X件」還是「任選X件」
                      // 如果標題中包含「任選」，則顯示「任選X件」
                      const hasSelectText = title && title.includes('任選');
                      let quantityText;
                      if (hasSelectText) {
                        // 如果標題包含「任選」，使用第一個贈品的數量（因為是任選X件，不是各X件）
                        const firstGiftQuantity = gifts[0]?.quantity || 1;
                        quantityText = `任選${firstGiftQuantity}件`;
                      } else {
                        // 如果標題不包含「任選」，計算總數量
                        const totalQuantity = gifts.reduce((sum, g) => sum + (g.quantity || 1), 0);
                        // 如果只有一個贈品項目，直接顯示數量（不顯示"各"）
                        if (gifts.length === 1) {
                          quantityText = `${totalQuantity}件`;
                        } else {
                          // 多個贈品項目，顯示"各X件"
                          quantityText = `各${totalQuantity}件`;
                        }
                      }
                      
                      // 獲取第一個贈品的樣式用於顯示圖片
                      const firstGift = gifts[0];
                      let styleIndex = -1;
                      let displayStyles = [];
                      
                      if (matchedProduct) {
                        // 收集所有樣式（包括多樣式任選的情況）
                        gifts.forEach(gift => {
                          if (gift.style && gift.style !== '--') {
                            // 檢查是否為多樣式（用斜線分隔）
                            if (gift.style.includes('/')) {
                              // 多樣式，拆分並加入
                              const styles = gift.style.split('/').map(s => s.trim()).filter(s => s);
                              styles.forEach(style => {
                                const idx = matchedProduct.styles?.findIndex((s, i) => {
                                  const styleDetail = matchedProduct.styleDetails?.[i];
                                  return s === style || (styleDetail && styleDetail.style === style);
                                });
                                if (idx >= 0 && !displayStyles.includes(matchedProduct.styles[idx])) {
                                  displayStyles.push(matchedProduct.styles[idx]);
                                } else if (idx < 0 && !displayStyles.includes(style)) {
                                  // 如果找不到匹配的樣式，直接顯示原始樣式名稱
                                  displayStyles.push(style);
                                }
                              });
                            } else {
                              // 單一樣式
                              const idx = matchedProduct.styles?.findIndex((s, i) => {
                                const styleDetail = matchedProduct.styleDetails?.[i];
                                return s === gift.style || (styleDetail && styleDetail.style === gift.style);
                              });
                              if (idx >= 0 && !displayStyles.includes(matchedProduct.styles[idx])) {
                                displayStyles.push(matchedProduct.styles[idx]);
                              } else if (idx < 0 && !displayStyles.includes(gift.style)) {
                                // 如果找不到匹配的樣式，直接顯示原始樣式名稱
                                displayStyles.push(gift.style);
                              }
                            }
                          }
                        });
                        
                        // 如果沒有收集到樣式，嘗試使用第一個贈品的樣式
                        if (displayStyles.length === 0 && firstGift.style && firstGift.style !== '--') {
                          if (firstGift.style.includes('/')) {
                            const styles = firstGift.style.split('/').map(s => s.trim()).filter(s => s);
                            displayStyles = styles;
                          } else {
                            displayStyles = [firstGift.style];
                          }
                        }
                        
                        // 找到第一個樣式的索引用於顯示圖片
                        if (firstGift.style && firstGift.style !== '--') {
                          const firstStyle = firstGift.style.includes('/') 
                            ? firstGift.style.split('/')[0].trim() 
                            : firstGift.style;
                          styleIndex = matchedProduct.styles?.findIndex((s, i) => {
                            const styleDetail = matchedProduct.styleDetails?.[i];
                            return s === firstStyle || (styleDetail && styleDetail.style === firstStyle);
                          });
                        }
                        
                        if (styleIndex < 0 && matchedProduct.styles && matchedProduct.styles.length > 0) {
                          styleIndex = 0;
                        }
                      } else {
                        // 沒有匹配到商品，也要顯示樣式
                        gifts.forEach(gift => {
                          if (gift.style && gift.style !== '--') {
                            if (gift.style.includes('/')) {
                              const styles = gift.style.split('/').map(s => s.trim()).filter(s => s);
                              styles.forEach(style => {
                                if (!displayStyles.includes(style)) {
                                  displayStyles.push(style);
                                }
                              });
                            } else {
                              if (!displayStyles.includes(gift.style)) {
                                displayStyles.push(gift.style);
                              }
                            }
                          }
                        });
                      }
                      
                      const styleDetail = styleIndex >= 0 && matchedProduct ? matchedProduct.styleDetails?.[styleIndex] : null;
                      const styleImage = styleDetail?.image || (matchedProduct ? matchedProduct.image : '');
                      
                      // 計算成本（使用第一個樣式的成本，如果有的話）
                      let costDisplay = '';
                      let shippingDisplay = '';
                      if (matchedProduct && styleDetail) {
                        const costKRW = styleDetail.costKoreanWon || matchedProduct.costKoreanWon || '';
                        const costJPY = styleDetail.costJPY || matchedProduct.costJPY || '';
                        const costTWD = styleDetail.costTWD || matchedProduct.costTWD || '';
                        const costTHB = styleDetail.costTHB || matchedProduct.costTHB || '';
                        const costRMB = styleDetail.costRMB || matchedProduct.costRMB || '';
                        const giftWeight = styleDetail.weight || matchedProduct.weight || '';
                        
                        // 計算台幣轉換後的成本
                        let convertedCostTWD = 0;
                        if (isChina && costRMB) {
                          convertedCostTWD = Math.ceil(Number(costRMB * exchangeRate));
                        } else if (isKorea && costKRW) {
                          convertedCostTWD = Math.ceil(Number(costKRW / exchangeRate));
                        } else if (isJapan && costJPY) {
                          convertedCostTWD = Math.ceil(Number(costJPY / exchangeRate));
                        } else if (isThailand && costTHB) {
                          convertedCostTWD = Math.ceil(Number(costTHB / exchangeRate));
                        } else if (costTWD) {
                          convertedCostTWD = Math.ceil(Number(costTWD));
                        }
                        
                        // 計算台幣換算+額外%數
                        const convertedCostWithExtra = convertedCostTWD > 0 
                          ? Math.ceil(convertedCostTWD * (1 + extraPercentage / 100))
                          : 0;
                        
                        // 顯示原始成本
                        if (isKorea && costKRW) {
                          costDisplay = `${costKRW} KRW`;
                        } else if (isJapan && costJPY) {
                          costDisplay = `${costJPY} JPY`;
                        } else if (isChina && costRMB) {
                          costDisplay = `${costRMB} RMB`;
                        } else if (costTHB) {
                          costDisplay = `${costTHB} THB`;
                        } else if (costTWD) {
                          costDisplay = `${costTWD} TWD`;
                        }
                        
                        // 計算運費（如果有重量）
                        if (giftWeight && convertedCostWithExtra > 0) {
                          const weightNum = Number(giftWeight);
                          if (isKorea && exchangeRate) {
                            const airSpec = {
                              twPerKg: airTaiwanPerKg,
                              intlKrwPerKg: airIntlKrwPerKg,
                            };
                            const transitSpec = {
                              twPerKg: transitTaiwanPerKg,
                              intlKrwPerKg: transitIntlKrwPerKg,
                            };
                            const seaSpec = {
                              twPerKg: seaTaiwanPerKg,
                              intlKrwPerKg: seaIntlKrwPerKg,
                            };
                            const airTotal = calculateShippingCost(
                              airSpec,
                              costKRW,
                              costJPY,
                              costTWD,
                              costTHB,
                              costRMB,
                              giftWeight,
                            );
                            const transitTotal = calculateShippingCost(
                              transitSpec,
                              costKRW,
                              costJPY,
                              costTWD,
                              costTHB,
                              costRMB,
                              giftWeight,
                            );
                            const seaTotal = calculateShippingCost(
                              seaSpec,
                              costKRW,
                              costJPY,
                              costTWD,
                              costTHB,
                              costRMB,
                              giftWeight,
                            );
                            const parts = [];
                            if (airTotal != null)
                              parts.push(`🚀直飛: ${airTotal} TWD`);
                            if (transitTotal != null)
                              parts.push(`✈️轉機: ${transitTotal} TWD`);
                            if (seaTotal != null)
                              parts.push(`🚢海快: ${seaTotal} TWD`);
                            shippingDisplay = parts.join(' ');
                          } else if (isJapan && airRate) {
                            // 日本：只有空運
                            const airShipping = Math.ceil(weightNum * airRate);
                            const airTotal = convertedCostWithExtra + airShipping;
                            shippingDisplay = `✈️空運: ${airTotal} TWD`;
                          } else if (isThailand && airRate) {
                            // 泰國：只有空運
                            const airShipping = Math.ceil(weightNum * airRate);
                            const airTotal = convertedCostWithExtra + airShipping;
                            shippingDisplay = `✈️空運: ${airTotal} TWD`;
                          } else if (isChina && shippingRate) {
                            // 中國：海快
                            const seaShipping = Math.ceil(weightNum * shippingRate * exchangeRate);
                            const seaTotal = convertedCostWithExtra + seaShipping;
                            shippingDisplay = `🚢海快: ${seaTotal} TWD`;
                          }
                        } else if (convertedCostWithExtra > 0) {
                          // 沒有重量，只顯示成本
                          shippingDisplay = `台幣: ${convertedCostWithExtra} TWD`;
                        }
                      }
                      
                      return (
                        <div key={groupIdx} style={{ padding: '0.75rem', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e5e5e5', position: 'relative' }}>
                          {title && (
                            <div style={{ fontSize: '0.85rem', color: '#d32f2f', fontWeight: '600', marginBottom: '0.5rem' }}>
                              {title}
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                            {styleImage && (
                              <img
                                src={styleImage}
                                alt={matchedProduct?.name || itemNumber}
                                style={{
                                  width: '60px',
                                  height: '60px',
                                  objectFit: 'cover',
                                  borderRadius: '4px',
                                  border: '1px solid #ddd',
                                  flexShrink: 0
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            )}
                            <div style={{ flex: 1 }}>
                              {matchedProduct ? (
                                <>
                                  <div style={{ fontWeight: '500', fontSize: '1rem', marginBottom: '0.25rem' }}>
                                    {matchedProduct.name}
                                  </div>
                                  <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.25rem' }}>
                                    料號: {itemNumber}
                                  </div>
                                  {displayStyles.length > 0 && (
                                    <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.25rem' }}>
                                      樣式: {displayStyles.join(' / ')}
                                    </div>
                                  )}
                                  <div style={{ fontSize: '0.9rem', color: '#333', fontWeight: '500', marginBottom: '0.25rem' }}>
                                    {quantityText}
                                  </div>
                                  {costDisplay && (
                                    <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.25rem' }}>
                                      成本: {costDisplay}
                                    </div>
                                  )}
                                  {shippingDisplay && (
                                    <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.25rem' }}>
                                      {shippingDisplay}
                                    </div>
                                  )}
                                  {matchedProductId && (
                                    <button
                                      onClick={() => window.open(`https://skybluelive.com/view/${country}/${matchedProductId}`, '_blank')}
                                      style={{
                                        marginTop: '0.5rem',
                                        padding: '0.25rem 0.75rem',
                                        fontSize: '0.85rem',
                                        background: '#fff',
                                        border: '1px solid #d32f2f',
                                        borderRadius: '4px',
                                        color: '#d32f2f',
                                        cursor: 'pointer'
                                      }}
                                    >
                                      查看
                                    </button>
                                  )}
                                </>
                              ) : (
                                <>
                                  <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.25rem' }}>
                                    料號: {itemNumber}
                                  </div>
                                  {displayStyles.length > 0 && (
                                    <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.25rem' }}>
                                      樣式: {displayStyles.join(' / ')}
                                    </div>
                                  )}
                                  <div style={{ fontSize: '0.9rem', color: '#333', fontWeight: '500' }}>
                                    {quantityText}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* 基本資訊 */}
          <div style={sectionStyle}>
            <div style={{ marginBottom: '1rem', display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <span style={labelStyle}>料號</span>
                <div style={valueStyle}>{itemNumber || '未設定'}</div>
              </div>
              <div style={{ flex: 1 }}>
                <span style={labelStyle}>廠商</span>
              {editingVendor ? (
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', position: 'relative' }}>
                  <input
                    type="text"
                    value={vendorInput}
                    onChange={(e) => setVendorInput(e.target.value)}
                    list="vendorList"
                    autoComplete="off"
                    style={{
                      flex: 1,
                      padding: '0.5rem',
                      border: '1px solid #e5e5e5',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                    onFocus={(e) => {
                      // 觸發下拉選單顯示
                      e.target.setAttribute('list', 'vendorList');
                    }}
                  />
                  <button
                    style={{
                      padding: '0.5rem 1rem',
                      background: '#333',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onClick={handleVendorSave}
                    disabled={isSavingVendor}
                    onMouseOver={(e) => { e.target.style.background = '#555'; }}
                    onMouseOut={(e) => { e.target.style.background = '#333'; }}
                  >
                    保存
                  </button>
                  <button
                    style={{
                      padding: '0.5rem 1rem',
                      background: '#f5f5f5',
                      color: '#333',
                      border: '1px solid #e5e5e5',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onClick={() => { setEditingVendor(false); setVendorInput(product.vendor || ''); }}
                    onMouseOver={(e) => { e.target.style.background = '#eee'; }}
                    onMouseOut={(e) => { e.target.style.background = '#f5f5f5'; }}
                  >
                    取消
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={valueStyle}>{product.vendor || '未設定'}</div>
                  <button
                    style={{
                      padding: '0.5rem 1rem',
                      background: '#f5f5f5',
                      color: '#333',
                      border: '1px solid #e5e5e5',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onClick={() => { setVendorBase(product); setVendorInput(product.vendor || ''); setEditingVendor(true); }}
                    onMouseOver={(e) => { e.target.style.background = '#eee'; }}
                    onMouseOut={(e) => { e.target.style.background = '#f5f5f5'; }}
                  >
                    編輯
                  </button>
                </div>
              )}
              </div>
            </div>
            <datalist id="vendorList">
              {sortedVendors.map(vendor => (
                <option key={vendor.id} value={vendor.name} />
              ))}
            </datalist>
            <div>
              <span style={labelStyle}>樣式</span>
              <div style={{ marginTop: '0.5rem' }}>{renderStyles()}</div>
            </div>
          </div>

          {/* 成本與重量資訊 */}
          {!isCrystal && (
          <>
          <div style={sectionStyle}>
            <span style={{ ...labelStyle, fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>成本與重量</span>
            {/* 如果是組合商品，計算所有子商品的成本和重量總和 */}
            {product.subProducts && product.subProducts.length > 0 ? (
              (() => {
                const comboCostWeight = calculateComboProductCostWeight();
                if (!comboCostWeight || comboCostWeight.hasMissingData) {
                  return (
                    <div>
                      <div style={{ marginBottom: '0.75rem' }}>
                        <span style={labelStyle}>重量</span>
                        <div style={valueStyle}>未設定 kg</div>
                      </div>
                      <div style={{ marginBottom: '0.75rem' }}>
                        <span style={labelStyle}>成本</span>
                        <div style={valueStyle}>未設定</div>
                      </div>
                    </div>
                  );
                }
                return (
                  <div>
                    {renderStyleCostInfo(
                      comboCostWeight.costKoreanWon,
                      comboCostWeight.costJPY,
                      comboCostWeight.costTWD,
                      comboCostWeight.costTHB,
                      null,
                      comboCostWeight.weight
                    )}
                  </div>
                );
              })()
            ) : product.styleDetails && product.styleDetails.length > 0 && product.styleDetails.some(detail => detail && detail.active !== false && (detail.costKoreanWon || detail.costJPY || detail.costTWD || detail.costTHB || detail.weight)) ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {/* 將樣式按成本和重量分組 */}
                {(() => {
                  const styleGroups = new Map();
                  (styles || []).forEach((styleText, idx) => {
                    const styleDetail = product.styleDetails?.[idx];
                    if (styleDetail?.active === false) return;
                    const styleCostKoreanWon = styleDetail?.costKoreanWon;
                    const styleCostJPY = styleDetail?.costJPY;
                    const styleCostTWD = styleDetail?.costTWD;
                    const styleCostTHB = styleDetail?.costTHB;
                    const styleWeight = styleDetail?.weight;
                    
                    const costWeightKey = JSON.stringify({
                      costKoreanWon: styleCostKoreanWon || costKoreanWon,
                      costJPY: styleCostJPY || costJPY,
                      costTWD: styleCostTWD || costTWD,
                      costTHB: styleCostTHB || costTHB,
                      weight: styleWeight || weight
                    });
                    
                    if (!styleGroups.has(costWeightKey)) {
                      styleGroups.set(costWeightKey, []);
                    }
                    styleGroups.get(costWeightKey).push(styleText);
                  });
                  
                  return Array.from(styleGroups.entries()).map(([costWeightKey, styleNames], groupIdx) => {
                    const costWeightData = JSON.parse(costWeightKey);
                    const useCostKoreanWon = costWeightData.costKoreanWon;
                    const useCostJPY = costWeightData.costJPY;
                    const useCostTWD = costWeightData.costTWD;
                    const useCostTHB = costWeightData.costTHB;
                    const useWeight = costWeightData.weight;
                    
                    const styleCostInfo = renderStyleCostInfo(
                      useCostKoreanWon,
                      useCostJPY,
                      useCostTWD,
                      useCostTHB,
                      null,
                      useWeight
                    );
                    
                    return (
                      <div key={groupIdx} style={{ padding: '0.75rem', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e5e5e5' }}>
                        <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem', fontWeight: '500' }}>
                          {styleNames.length > 1 ? `樣式: ${styleNames.join('、')}` : `樣式: ${styleNames[0]}`}
                        </div>
                        {styleCostInfo}
                      </div>
                    );
                  });
                })()}
              </div>
            ) : (
              /* 如果沒有樣式級別的成本和重量，使用商品級別的值 */
              <div>
                <div style={{ marginBottom: '0.75rem' }}>
                  <span style={labelStyle}>重量</span>
                  <div style={valueStyle}>{weight || '未設定'} kg</div>
                </div>
                {renderCostInfo()}
              </div>
            )}
          </div>

          {/* 運費資訊 */}
          <div style={sectionStyle}>
            <span style={{ ...labelStyle, fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>運費計算</span>
            {/* 如果是組合商品，計算所有子商品的成本和重量總和來計算運費 */}
            {product.subProducts && product.subProducts.length > 0 ? (
              (() => {
                const comboCostWeight = calculateComboProductCostWeight();
                if (!comboCostWeight || comboCostWeight.hasMissingData) {
                  return (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
                      <div style={{ padding: '0.75rem', background: '#f8f9fa', borderRadius: '8px' }}>
                        <span style={labelStyle}>{isKorea ? '🚀 直飛空運' : '🚀 空運成本'}</span>
                        <div style={{ ...valueStyle, fontSize: '1.2rem' }}>
                          未設定 {targetCurrencyCode}
                        </div>
                      </div>
                      {isKorea && (
                        <>
                          <div style={{ padding: '0.75rem', background: '#f8f9fa', borderRadius: '8px' }}>
                            <span style={labelStyle}>🚢 海快</span>
                            <div style={{ ...valueStyle, fontSize: '1.2rem' }}>
                              未設定 {targetCurrencyCode}
                            </div>
                          </div>
                          <div style={{ padding: '0.75rem', background: '#f8f9fa', borderRadius: '8px' }}>
                            <span style={labelStyle}>✈️ 轉機空運</span>
                            <div style={{ ...valueStyle, fontSize: '1.2rem' }}>
                              未設定 {targetCurrencyCode}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  );
                }
                
                // 計算運費結果（使用組合商品的成本和重量總和）
                const airFreightSpec = isKorea
                  ? { twPerKg: airTaiwanPerKg, intlKrwPerKg: airIntlKrwPerKg }
                  : airRate;
                const airCost = calculateShippingCost(
                  airFreightSpec,
                  comboCostWeight.costKoreanWon,
                  comboCostWeight.costJPY,
                  comboCostWeight.costTWD,
                  comboCostWeight.costTHB,
                  null,
                  comboCostWeight.weight
                );
                const seaCost = isKorea
                  ? calculateShippingCost(
                      { twPerKg: seaTaiwanPerKg, intlKrwPerKg: seaIntlKrwPerKg },
                      comboCostWeight.costKoreanWon,
                      comboCostWeight.costJPY,
                      comboCostWeight.costTWD,
                      comboCostWeight.costTHB,
                      null,
                      comboCostWeight.weight
                    )
                  : null;
                const transitCost = isKorea
                  ? calculateShippingCost(
                      { twPerKg: transitTaiwanPerKg, intlKrwPerKg: transitIntlKrwPerKg },
                      comboCostWeight.costKoreanWon,
                      comboCostWeight.costJPY,
                      comboCostWeight.costTWD,
                      comboCostWeight.costTHB,
                      null,
                      comboCostWeight.weight
                    )
                  : null;
                
                const shippingResults = [
                  { label: isKorea ? '🚀 直飛空運' : '🚀 空運成本', value: airCost },
                  ...(isKorea ? [
                    { label: '🚢 海快', value: seaCost },
                    { label: '✈️ 轉機空運', value: transitCost }
                  ] : [])
                ].filter(item => item.value !== null);
                
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
                    {shippingResults.map((result, idx) => (
                      <div 
                        key={idx} 
                        style={{ 
                          padding: '0.75rem', 
                          background: '#f8f9fa', 
                          borderRadius: '8px',
                          border: '1px solid #e5e5e5'
                        }}
                      >
                        <span style={labelStyle}>{result.label}</span>
                        <div style={{ ...valueStyle, fontSize: '1.2rem' }}>
                          {result.value || '未設定'} {targetCurrencyCode}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()
            ) : product.styleDetails && product.styleDetails.length > 0 && product.styleDetails.some(detail => detail && detail.active !== false && (detail.costKoreanWon || detail.costJPY || detail.costTWD || detail.costTHB || detail.weight)) ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                {/* 將樣式按成本和重量分組 */}
                {(() => {
                  const styleGroups = new Map();
                  (styles || []).forEach((styleText, idx) => {
                    const styleDetail = product.styleDetails?.[idx];
                    if (styleDetail?.active === false) return;
                    const styleCostKoreanWon = styleDetail?.costKoreanWon;
                    const styleCostJPY = styleDetail?.costJPY;
                    const styleCostTWD = styleDetail?.costTWD;
                    const styleCostTHB = styleDetail?.costTHB;
                    const styleWeight = styleDetail?.weight;
                    
                    const costWeightKey = JSON.stringify({
                      costKoreanWon: styleCostKoreanWon || costKoreanWon,
                      costJPY: styleCostJPY || costJPY,
                      costTWD: styleCostTWD || costTWD,
                      costTHB: styleCostTHB || costTHB,
                      weight: styleWeight || weight
                    });
                    
                    if (!styleGroups.has(costWeightKey)) {
                      styleGroups.set(costWeightKey, []);
                    }
                    styleGroups.get(costWeightKey).push(styleText);
                  });
                  
                  const groupArray = Array.from(styleGroups.entries());
                  return (
                    <>
                      {groupArray.map(([costWeightKey, styleNames], groupIdx) => {
                        const costWeightData = JSON.parse(costWeightKey);
                        const useCostKoreanWon = costWeightData.costKoreanWon;
                        const useCostJPY = costWeightData.costJPY;
                        const useCostTWD = costWeightData.costTWD;
                        const useCostTHB = costWeightData.costTHB;
                        const useWeight = costWeightData.weight;
                        
                        // 計算運費結果（使用樣式級別的成本和重量，如果沒有則使用商品級別的值）
                        const airFreightSpec = isKorea
                          ? { twPerKg: airTaiwanPerKg, intlKrwPerKg: airIntlKrwPerKg }
                          : airRate;
                        const airCost = calculateShippingCost(
                          airFreightSpec, 
                          useCostKoreanWon || costKoreanWon, 
                          useCostJPY || costJPY, 
                          useCostTWD || costTWD, 
                          useCostTHB || costTHB, 
                          null, 
                          useWeight || weight
                        );
                        const seaCost = isKorea ? calculateShippingCost(
                          { twPerKg: seaTaiwanPerKg, intlKrwPerKg: seaIntlKrwPerKg }, 
                          useCostKoreanWon || costKoreanWon, 
                          useCostJPY || costJPY, 
                          useCostTWD || costTWD, 
                          useCostTHB || costTHB, 
                          null, 
                          useWeight || weight
                        ) : null;
                        const transitCost = isKorea ? calculateShippingCost(
                          { twPerKg: transitTaiwanPerKg, intlKrwPerKg: transitIntlKrwPerKg }, 
                          useCostKoreanWon || costKoreanWon, 
                          useCostJPY || costJPY, 
                          useCostTWD || costTWD, 
                          useCostTHB || costTHB, 
                          null, 
                          useWeight || weight
                        ) : null;
                        
                        const shippingResults = [
                          { label: isKorea ? '🚀 直飛空運' : '🚀 空運成本', value: airCost },
                          ...(isKorea ? [
                            { label: '🚢 海快', value: seaCost },
                            { label: '✈️ 轉機空運', value: transitCost }
                          ] : [])
                        ].filter(item => item.value !== null);
                        
                        return (
                          <div key={groupIdx} style={{ 
                            padding: '0.75rem', 
                            background: '#f8f9fa', 
                            borderRadius: '8px', 
                            border: '1px solid #e5e5e5',
                            flex: '0 0 calc(50% - 0.375rem)',
                            minWidth: 'calc(50% - 0.375rem)'
                          }}>
                            <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.75rem', fontWeight: '500' }}>
                              {styleNames.length > 1 ? `樣式: ${styleNames.join('、')}` : `樣式: ${styleNames[0]}`}
                            </div>
                            {/* 運費結果垂直排列 */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                              {shippingResults.map((result, idx) => (
                                <div 
                                  key={idx} 
                                  style={{ 
                                    padding: '0.75rem', 
                                    background: '#fff', 
                                    borderRadius: '6px', 
                                    border: '1px solid #e5e5e5'
                                  }}
                                >
                                  <span style={{ ...labelStyle, fontSize: '0.85rem', display: 'block', marginBottom: '0.25rem' }}>{result.label}</span>
                                  <div style={{ ...valueStyle, fontSize: '1.1rem' }}>{result.value} {targetCurrencyCode}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </>
                  );
                })()}
              </div>
            ) : (
              /* 如果沒有樣式級別的成本和重量，使用商品級別的值 */
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
                <div style={{ padding: '0.75rem', background: '#f8f9fa', borderRadius: '8px' }}>
                  <span style={labelStyle}>{isKorea ? '🚀 直飛空運' : '🚀 空運成本'}</span>
                  <div style={{ ...valueStyle, fontSize: '1.2rem' }}>
                    {calculateShippingCost(
                      isKorea
                        ? { twPerKg: airTaiwanPerKg, intlKrwPerKg: airIntlKrwPerKg }
                        : airRate,
                      costKoreanWon,
                      costJPY,
                      costTWD,
                      costTHB,
                      costRMB,
                      weight,
                    ) || '未設定'}{' '}
                    {targetCurrencyCode}
                  </div>
                </div>
                {isKorea && (
                  <>
                    <div style={{ padding: '0.75rem', background: '#f8f9fa', borderRadius: '8px' }}>
                      <span style={labelStyle}>🚢 海快</span>
                      <div style={{ ...valueStyle, fontSize: '1.2rem' }}>
                        {calculateShippingCost(
                          { twPerKg: seaTaiwanPerKg, intlKrwPerKg: seaIntlKrwPerKg },
                          costKoreanWon,
                          costJPY,
                          costTWD,
                          costTHB,
                          costRMB,
                          weight,
                        ) || '未設定'}{' '}
                        {targetCurrencyCode}
                      </div>
                    </div>
                    <div style={{ padding: '0.75rem', background: '#f8f9fa', borderRadius: '8px' }}>
                      <span style={labelStyle}>✈️ 轉機空運</span>
                      <div style={{ ...valueStyle, fontSize: '1.2rem' }}>
                        {calculateShippingCost(
                          { twPerKg: transitTaiwanPerKg, intlKrwPerKg: transitIntlKrwPerKg },
                          costKoreanWon,
                          costJPY,
                          costTWD,
                          costTHB,
                          costRMB,
                          weight,
                        ) || '未設定'}{' '}
                        {targetCurrencyCode}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          </>
          )}

          {/* 操作按鈕 */}
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem', flexWrap: 'wrap' }}>
            <button 
              onClick={() => navigate(`/edit/${country}/${id}`)}
              style={{
                flex: 1,
                minWidth: '120px',
                background: '#333',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '0.75rem',
                fontSize: '1rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => { e.target.style.background = '#555'; }}
              onMouseOut={(e) => { e.target.style.background = '#333'; }}
            >
              編輯
            </button>
            <button 
              onClick={() => setShowHistory(!showHistory)}
              style={{
                flex: 1,
                minWidth: '120px',
                background: '#f5f5f5',
                color: '#333',
                border: '1px solid #e5e5e5',
                borderRadius: '8px',
                padding: '0.75rem',
                fontSize: '1rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => { e.target.style.background = '#eee'; }}
              onMouseOut={(e) => { e.target.style.background = '#f5f5f5'; }}
            >
              歷史價格
            </button>
            <button
              type="button"
              onClick={() => {
                if (!photoDirty) { setPhotoDrafts(buildProductPhotos(product)); setPhotoBase(product); }
                setShowPhotoManager(true);
              }}
              style={{
                flex: 1,
                minWidth: '120px',
                border: '1px solid #c7d2fe',
                background: '#eef2ff',
                color: '#4338ca',
                borderRadius: '8px',
                padding: '0.75rem',
                fontSize: '1rem',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => { e.target.style.background = '#e0e7ff'; }}
              onMouseOut={(e) => { e.target.style.background = '#eef2ff'; }}
            >
              商品相簿
            </button>
            <button 
              onClick={handleOpenDeleteConfirm}
              disabled={isDeleting || isPreparationProduct}
              title={isPreparationProduct ? PREPARATION_DELETE_MESSAGE : '刪除舊版商品'}
              style={{
                flex: 1,
                minWidth: '120px',
                background: isDeleting || isPreparationProduct ? '#ccc' : '#dc3545',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '0.75rem',
                fontSize: '1rem',
                fontWeight: '500',
                cursor: isDeleting || isPreparationProduct ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                opacity: isDeleting || isPreparationProduct ? 0.6 : 1
              }}
              onMouseOver={(e) => { if (!isDeleting && !isPreparationProduct) e.target.style.background = '#c82333'; }}
              onMouseOut={(e) => { if (!isDeleting && !isPreparationProduct) e.target.style.background = '#dc3545'; }}
            >
              {isDeleting ? '刪除中...' : isPreparationProduct ? '已啟用備播，無法刪除' : '🗑️ 刪除商品'}
            </button>
          </div>

          {isPreparationProduct && (
            <p role="note" style={{ color: '#64748b', fontSize: '0.85rem', lineHeight: 1.6 }}>{PREPARATION_DELETE_MESSAGE}</p>
          )}

          {/* 刪除確認對話框 */}
          {showDeleteConfirm && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000,
              padding: '1rem'
            }}>
              <div style={{
                background: '#fff',
                borderRadius: '12px',
                padding: '2rem',
                maxWidth: '400px',
                width: '100%',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
              }}>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  marginBottom: '1rem',
                  color: '#dc3545',
                  textAlign: 'center'
                }}>
                  ⚠️ 確認刪除商品
                </h3>
                
                <div style={{
                  marginBottom: '1.5rem',
                  padding: '1rem',
                  background: '#f8f9fa',
                  borderRadius: '8px'
                }}>
                  <p style={{ margin: '0.5rem 0', fontSize: '1rem' }}>
                    <strong>商品名稱：</strong>{name || '未命名'}
                  </p>
                  <p style={{ margin: '0.5rem 0', fontSize: '1rem' }}>
                    <strong>料號：</strong>{itemNumber || '無'}
                  </p>
                  <p style={{ 
                    margin: '1rem 0 0 0', 
                    fontSize: '0.9rem', 
                    color: '#dc3545',
                    fontWeight: '500'
                  }}>
                    ⚠️ 此操作無法復原！
                  </p>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    marginBottom: '0.5rem',
                    color: '#333'
                  }}>
                    請輸入驗證碼以確認刪除：
                  </label>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    marginBottom: '0.5rem'
                  }}>
                    <div style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: '#f0f0f0',
                      borderRadius: '8px',
                      textAlign: 'center',
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      letterSpacing: '0.2rem',
                      color: '#dc3545',
                      fontFamily: 'monospace',
                      border: '2px solid #dc3545'
                    }}>
                      {deleteVerificationCode}
                    </div>
                  </div>
                  <input
                    type="text"
                    value={deleteCodeInput}
                    onChange={(e) => {
                      // 只允許輸入數字，最多4位
                      const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                      setDeleteCodeInput(value);
                    }}
                    placeholder="輸入4位數驗證碼"
                    autoFocus
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      fontSize: '1.25rem',
                      textAlign: 'center',
                      border: '2px solid #e5e5e5',
                      borderRadius: '8px',
                      fontFamily: 'monospace',
                      letterSpacing: '0.2rem',
                      boxSizing: 'border-box'
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && deleteCodeInput.length === 4) {
                        handleDeleteProduct();
                      }
                    }}
                  />
                </div>

                <div style={{
                  display: 'flex',
                  gap: '0.75rem'
                }}>
                  <button
                    onClick={handleCloseDeleteConfirm}
                    disabled={isDeleting}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: '#f5f5f5',
                      color: '#333',
                      border: '1px solid #e5e5e5',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      fontWeight: '500',
                      cursor: isDeleting ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => { if (!isDeleting) e.target.style.background = '#eee'; }}
                    onMouseOut={(e) => { if (!isDeleting) e.target.style.background = '#f5f5f5'; }}
                  >
                    取消
                  </button>
                  <button
                    onClick={handleDeleteProduct}
                    disabled={isDeleting || deleteCodeInput.length !== 4}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: (isDeleting || deleteCodeInput.length !== 4) ? '#ccc' : '#dc3545',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      fontWeight: '500',
                      cursor: (isDeleting || deleteCodeInput.length !== 4) ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => { 
                      if (!isDeleting && deleteCodeInput.length === 4) {
                        e.target.style.background = '#c82333';
                      }
                    }}
                    onMouseOut={(e) => { 
                      if (!isDeleting && deleteCodeInput.length === 4) {
                        e.target.style.background = '#dc3545';
                      }
                    }}
                  >
                    {isDeleting ? '刪除中...' : '確認刪除'}
                  </button>
                </div>
              </div>
            </div>
          )}
          {showHistory && (
            <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e5e5' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: '#333' }}>
                歷史價格（由新到舊）
              </h3>
              {renderHistoryList()}
            </div>
          )}
        </div>

        {/* 在最底部加入搜尋區塊 */}
        <div style={{ ...cardStyle, marginTop: '1rem' }}>
          <h3 style={{ textAlign: 'center', fontSize: '1.1rem', fontWeight: '600', marginBottom: '1rem', color: '#666' }}>繼續搜尋其他商品</h3>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={(e) => e.target.select()}
            placeholder="輸入商品名稱、料號或條碼末5碼"
            style={{
              width: '100%',
              padding: '0.75rem',
              fontSize: '1rem',
              border: '1px solid #e5e5e5',
              borderRadius: '8px',
              marginBottom: '0.75rem',
              boxSizing: 'border-box'
            }}
          />
          <button
            onClick={handleSearchClick}
            style={{
              width: '100%',
              background: '#333',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '0.75rem',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => { e.target.style.background = '#555'; }}
            onMouseOut={(e) => { e.target.style.background = '#333'; }}
          >
            搜尋
          </button>
        </div>
      </div>
      {showPhotoManager && <ProductAlbumDialog product={product} photos={photoDrafts} dirty={photoDirty}
        busy={isSavingPhotos} reading={isReadingPhotos} error={photoError} url={newPhotoUrl}
        onUrlChange={setNewPhotoUrl} onAddUrl={addPhotoDraftByUrl} onFiles={handlePhotoDraftUpload}
        onRemove={removeDraftPhoto} onSave={savePhotoDrafts} onClose={() => setShowPhotoManager(false)} />}
    </div>
  );
}

export default ViewProduct;
